import axios, {
  type Method,
  AxiosError,
  AxiosResponse,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { store } from '@whelk/store';
import { isNative } from '@whelk/const/src/env';
import { logoutCallback } from '@whelk/utils/src/logout';
import { openLoginModal } from '@whelk/utils/src/login';
import { LocalStorageTokenKey } from '@whelk/const/src/token';
import { NEED_LOGIN_ERROR_CODE, USER_BANNED_ERROR_CODE } from '@whelk/services/src/type/chat';
import type { ApiResp, StatusInfo } from '@whelk/services/src/common';
import { setServerAlertStatus } from '@whelk/store/slices/layoutSlice';
import { COMMON_SERVER_TIMEOUT, UNAUTHORIZED_CODE } from '@whelk/const/src/services';
import i18n, { getTextByLang } from '@whelk/utils/src/i18n';
import { publicEncrypt } from './encrypt';
import {
  getNativeNetworkParams,
  getNativePublicHeader,
  handleGeneralParams,
  addFetchUrlPublicParams,
} from './publicParams';
import { openBannedAppealModal } from '@whelk/utils/src/ban';
import { Toast } from '@whelk/components';

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  showToast?: boolean;
}

const defaultConfig: CustomAxiosRequestConfig = {
  timeout: COMMON_SERVER_TIMEOUT,
  timeoutErrorMessage: '请求超时',
  showToast: true,
  headers: {
    'Content-type': 'application/json',
  },
};

const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  config.headers.token = localStorage.getItem(LocalStorageTokenKey);
  const time = Date.parse(new Date().toString());
  let publicParams;
  if (isNative) {
    publicParams = getNativeNetworkParams(time);
    config.headers = {
      ...getNativePublicHeader(),
      ...config.headers,
    };
  } else {
    publicParams = handleGeneralParams(time);
  }
  config.params = {
    ...config.params,
    ...publicParams,
  };

  const hasSearchParamsPath = addFetchUrlPublicParams(config.url ?? '', config.params);
  config.headers.yy = publicEncrypt({
    time: time,
    body: config.data,
    hasSearchParamsPath,
    method: config.method as Method,
    bodyToYY: config?.headers?.yy || null,
  });

  return config;
};

const handleServerAlert = (statusInfo: StatusInfo) => {
  const currentServerAlert = store.getState().layout.serverAlertStatus;
  if (currentServerAlert !== statusInfo.serverAlert) {
    store.dispatch(setServerAlertStatus(statusInfo.serverAlert));
  }
};

const responseInterceptor = (response: AxiosResponse<ApiResp<unknown>>) => {
  const { data } = response;
  if (data.statusInfo) {
    handleServerAlert(data.statusInfo);
  }
  if (data.statusInfo && data.statusInfo.code !== 0) {
    if (data.statusInfo?.code === USER_BANNED_ERROR_CODE) {
      openBannedAppealModal(data.statusInfo?.message);
      return response;
    }
    throw data.statusInfo;
  }
  return response;
};

const commonErrorHandler = (error: AxiosError<ApiResp<unknown>>) => {
  const response = error.response;
  const is401 = response?.status === UNAUTHORIZED_CODE;
  const statusInfo = response?.data?.statusInfo;
  const config = error.config as CustomAxiosRequestConfig;
  const showToast = config?.showToast && statusInfo?.code !== USER_BANNED_ERROR_CODE;

  if (is401) {
    // todo 不要写单独的逻辑处理，抽离公共参数，判断是否处理
    // token续期接口和手机登录接口报401不需要处理
    if (config?.url === '/v1/api/user/renewal' || config?.url === '/v1/api/user/login/phone') {
      throw statusInfo ? statusInfo : error;
    }
    if (store.getState().user.isLogin) {
      const toastText =
        statusInfo?.message ?? getTextByLang('toast_login_expired_relogin', i18n.language);
      logoutCallback({
        delay: 1000,
        toastText: showToast ? toastText : '',
      });
    } else {
      logoutCallback();
    }
    throw statusInfo ? statusInfo : error;
  } else if (statusInfo) {
    handleServerAlert(statusInfo);
    if (statusInfo?.code === NEED_LOGIN_ERROR_CODE) {
      openLoginModal();
    }
    throw statusInfo;
  } else {
    const msg = error.message
      ?.replace('Network Error', getTextByLang('toast_network_error', i18n.language))
      ?.replace('Request failed with status code', '请求遇到错误，状态码')
      ?.replace('request aborted', '');

    if (showToast && msg) {
      Toast.show(msg);
    }

    throw error;
  }
};

const instance: AxiosInstance = axios.create(defaultConfig);
instance.interceptors.request.use(requestInterceptor);
instance.interceptors.response.use(responseInterceptor, commonErrorHandler);

export const replaceNetworkError = (p: string) => {
  if (typeof p === 'string') {
    p = p
      ?.replace('Network Error', getTextByLang('toast_network_error', i18n.language))
      ?.replace('Request failed with status code', '请求遇到错误，状态码');
  }
  return p;
};

export default instance;
